/** DailyQuestStore — デイリークエスト 3 本 / 24h リセット管理。
 *  Reference: docs/PROPOSALS/RETENTION.md §R1
 *  - localStorage `ck.dailyQuests`: { date, quests[] }
 *  - 3 種クエスト自動生成(日付ローテーション)
 *  - 各完走 +50 Color、全 3 完走 +200 Color + ブースター 1
 *  - Pay-to-Win 禁止: ステージスキップ等は含まない(Skill Honored 準拠)
 */

export type QuestType = 'clearStages' | 'highCoverage' | 'noBooster';

export interface Quest {
  /** ユニーク ID(当日のインデックスベース) */
  readonly id: string;
  /** クエスト種別 */
  readonly type: QuestType;
  /** 達成目標値 */
  readonly target: number;
  /** 現在の進捗 */
  progress: number;
  /** 報酬受取済みか */
  claimed: boolean;
}

export interface DailyQuestState {
  /** 発行日付 yyyy-mm-dd */
  date: string;
  quests: Quest[];
}

export interface QuestClaimResult {
  /** 付与 Color */
  colorGained: number;
  /** 付与ブースター種別(全完走ボーナス時のみ) */
  boosterGained: boolean;
  /** クエスト ID が存在しなかった / 未達成 / 既受取 */
  error?: 'not_found' | 'not_complete' | 'already_claimed';
}

export interface StageClearedPayload {
  /** クリアしたステージ ID (1-01 等) */
  stageId: string;
  /** 最終占領率 0.0–1.0 */
  coverage: number;
  /** ブースターを使用したか */
  usedBoosters: boolean;
}

const STORAGE_KEY = 'ck.dailyQuests';

/** 各クエスト単体クリア時の Color 報酬 */
const QUEST_SINGLE_REWARD = 50;
/** 3 本全完走ボーナス Color */
const QUEST_FULL_BONUS_COLOR = 200;

/** highCoverage クエストの閾値 */
const HIGH_COVERAGE_THRESHOLD = 0.85;

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 日付文字列から決定論的に当日クエストを生成する(サーバー不要) */
function generateQuests(date: string): Quest[] {
  // 日付数値からローテーション index を決める
  const seed = date.split('-').reduce((acc, part) => acc + parseInt(part, 10), 0);

  // 3 種クエスト定義テンプレート(Skill Honored: Hard 系も選択可・強制ではない)
  const templates: Array<{ type: QuestType; target: number }> = [
    { type: 'clearStages',  target: 2 },
    { type: 'clearStages',  target: 3 },
    { type: 'highCoverage', target: 1 },
    { type: 'noBooster',    target: 1 },
    { type: 'clearStages',  target: 1 },
    { type: 'highCoverage', target: 2 },
    { type: 'noBooster',    target: 2 },
  ];

  // seed を使って 3 つを選ぶ(重複なし)
  const indices: number[] = [];
  let s = seed;
  while (indices.length < 3) {
    const idx = ((s % templates.length) + templates.length) % templates.length;
    if (!indices.includes(idx)) {
      indices.push(idx);
    }
    s = Math.floor(s / templates.length) + (s % 7) + 1;
    // 無限ループ防止
    if (s > 1_000_000) s = s % 997;
  }

  return indices.map((templateIdx, i) => {
    const tmpl = templates[templateIdx]!;
    return {
      id: `dq-${date}-${i}`,
      type: tmpl.type,
      target: tmpl.target,
      progress: 0,
      claimed: false,
    };
  });
}

const VALID_QUEST_TYPES: readonly QuestType[] = ['clearStages', 'highCoverage', 'noBooster'];

export class DailyQuestStore {
  private state: DailyQuestState;

  constructor() {
    this.state = this.loadOrRefresh();
  }

  private loadOrRefresh(): DailyQuestState {
    const today = todayStr();
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<DailyQuestState>;
        if (
          parsed.date === today &&
          Array.isArray(parsed.quests) &&
          parsed.quests.length === 3
        ) {
          const quests = (parsed.quests as Partial<Quest>[]).map((q, i): Quest => ({
            id: typeof q.id === 'string' ? q.id : `dq-${today}-${i}`,
            type: VALID_QUEST_TYPES.includes(q.type as QuestType)
              ? (q.type as QuestType)
              : 'clearStages',
            target: typeof q.target === 'number' && q.target > 0 ? q.target : 1,
            progress: typeof q.progress === 'number' && q.progress >= 0 ? q.progress : 0,
            claimed: typeof q.claimed === 'boolean' ? q.claimed : false,
          }));
          return { date: today, quests };
        }
      }
    } catch {
      /* fall through to generate */
    }
    return this.freshState(today);
  }

  private freshState(date: string): DailyQuestState {
    return { date, quests: generateQuests(date) };
  }

  private save(): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* noop(プライベートモード等) */
    }
  }

  /** 今日のクエスト一覧を返す(日付が変わっていたら自動リフレッシュ) */
  getDailyQuests(): readonly Quest[] {
    const today = todayStr();
    if (this.state.date !== today) {
      this.state = this.freshState(today);
      this.save();
    }
    return this.state.quests;
  }

  /** ステージクリア時に呼ぶ。各クエスト進捗を更新する。 */
  onStageCleared(payload: StageClearedPayload): void {
    const today = todayStr();
    if (this.state.date !== today) {
      this.state = this.freshState(today);
    }

    for (const quest of this.state.quests) {
      if (quest.claimed) continue;

      switch (quest.type) {
        case 'clearStages':
          quest.progress = Math.min(quest.target, quest.progress + 1);
          break;
        case 'highCoverage':
          if (payload.coverage >= HIGH_COVERAGE_THRESHOLD) {
            quest.progress = Math.min(quest.target, quest.progress + 1);
          }
          break;
        case 'noBooster':
          if (!payload.usedBoosters) {
            quest.progress = Math.min(quest.target, quest.progress + 1);
          }
          break;
      }
    }

    this.save();
  }

  /** クエスト報酬を受け取る。達成済み・未受取の場合のみ有効。
   *  全 3 本クレーム完了時に +200 Color + boosterGained=true を返す。
   */
  claim(questId: string): QuestClaimResult {
    const today = todayStr();
    if (this.state.date !== today) {
      this.state = this.freshState(today);
      this.save();
    }

    const quest = this.state.quests.find((q) => q.id === questId);
    if (!quest) {
      return { colorGained: 0, boosterGained: false, error: 'not_found' };
    }
    if (quest.progress < quest.target) {
      return { colorGained: 0, boosterGained: false, error: 'not_complete' };
    }
    if (quest.claimed) {
      return { colorGained: 0, boosterGained: false, error: 'already_claimed' };
    }

    quest.claimed = true;
    this.save();

    // 全 3 本クレーム完了チェック
    const allClaimed = this.state.quests.every((q) => q.claimed);
    return {
      colorGained: QUEST_SINGLE_REWARD + (allClaimed ? QUEST_FULL_BONUS_COLOR : 0),
      boosterGained: allClaimed,
      error: undefined,
    };
  }

  /** クエスト種別の日本語ラベルを返す(UI 表示用) */
  static getQuestLabel(quest: Quest): string {
    switch (quest.type) {
      case 'clearStages':
        return `ステージを ${quest.target} 回クリア`;
      case 'highCoverage':
        return `占領率 ${Math.round(HIGH_COVERAGE_THRESHOLD * 100)}% 以上で ${quest.target} 回勝利`;
      case 'noBooster':
        return `ブースターを使わず ${quest.target} 回クリア`;
    }
  }

  /** デバッグ・テスト用: ステートをリセット */
  _reset(): void {
    this.state = this.freshState(todayStr());
    this.save();
  }
}
